'use client';

import { AuthContextProvider } from '@/contexts/AuthContext/AuthContext';
import EventEmitterProvider from '@/contexts/EventEmitterContext/EventEmitterProvider';
import { MediaQueryContextProvider } from '@/contexts/MediaQueryContext';
import { MetrisUIProvider } from '@andritzot/metris-web-ui/context';
import { useTheme } from '@andritzot/metris-web-utils/store/useTheme';
import PiwikProProvider from '@piwikpro/next-piwik-pro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from 'next-auth/react';
import dynamic from 'next/dynamic';
import { PrimeReactProvider } from 'primereact/api';
import { useEffect, useState } from 'react';

const isDev = process.env.NODE_ENV === 'development';

const ReactQueryDevtools = isDev
  ? dynamic(() => import('@tanstack/react-query-devtools').then((mod) => mod.ReactQueryDevtools), { ssr: false })
  : () => null;

const ClickToComponent = isDev
  ? dynamic(
      () =>
        import('click-to-react-component').then(
          (mod: { ClickToComponent: React.ComponentType }) => mod.ClickToComponent,
        ),
      { ssr: false },
    )
  : () => null;

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  });
}

export const Providers = ({ children }: { children: React.ReactNode }) => {
  const [queryClient] = useState(makeQueryClient);
  const { switchTheme } = useTheme();

  useEffect(() => {
    switchTheme('light');
  }, [switchTheme]);

  return (
    <PrimeReactProvider value={{ zIndex: { modal: 9100, overlay: 9200, menu: 9300, tooltip: 9400 } }}>
      <EventEmitterProvider>
        <SessionProvider refetchOnWindowFocus={false} refetchInterval={0}>
          <QueryClientProvider client={queryClient}>
            <MetrisUIProvider>
              <AuthContextProvider>
                <MediaQueryContextProvider>
                  {isDev ? (
                    children
                  ) : (
                    <PiwikProProvider
                      containerUrl={process.env.NEXT_PUBLIC_CONTAINER_URL ?? ''}
                      containerId={process.env.NEXT_PUBLIC_CONTAINER_ID ?? ''}
                    >
                      {children}
                    </PiwikProProvider>
                  )}
                </MediaQueryContextProvider>
                <ReactQueryDevtools initialIsOpen={false} />
                <ClickToComponent />
              </AuthContextProvider>
            </MetrisUIProvider>
          </QueryClientProvider>
        </SessionProvider>
      </EventEmitterProvider>
    </PrimeReactProvider>
  );
};
